/**
 * Tipos do Supervisor Dashboard (Brief 3 V2 Tela 1 — S10).
 *
 * Hand-written derivados do contrato REAL do engine (R15 triangulado):
 *   apps/engine/src/modules/supervisor-dashboard/dashboard.types.ts
 *   apps/engine/src/modules/supervisor-dashboard/supervisor-dashboard.controller.ts
 *   apps/engine/src/modules/websocket/websocket.types.ts
 *
 * NÃO redefinir tipos canônicos já existentes em src/lib/types.ts — importar e estender.
 * Item candidate 318: avaliar typegen OpenAPI (Q1 da spec) como follow-up.
 */

// ─── KPIs ───────────────────────────────────────────────────────────────
// R15: o agregador S10 cobre 10 dos 11 KPIs do Briefing §6 pós-Onda B (Item
// 317): KPI 6 (aceite sugestão IA) + KPI 11 (awaiting médio) materializados.
// KPI 8 (retomada por gatilho) permanece sem campo — sem fonte de dado no
// modelo; T1 exibe placeholder honesto.
export interface SupervisorDashboardKPIs {
  conversationsActive: number;
  queueDepth: number;
  p1Active: number;
  attendantsOnline: number;
  attendantsTotal: number;
  csatAverage: number | null;
  aiConducting: number;
  slaCumprimento: number;
  conflictAlertsActive: number;
  oldestPendencyOverdueMinutes: number | null;
  // ─── Item 317 Onda B (opcionais — retrocompat até o backend deployar) ───
  /** KPI 6 — taxa de aceite de sugestão IA (0..1), últimos 7d. null se sem decisões. */
  aiSuggestionAcceptanceRate?: number | null;
  /**
   * Conversas abertas (não terminais) — base do «% das abertas» do KPI «IA dirigindo».
   * Opcional até o backend deployar (noturna 04/Set/2026, A-06).
   */
  conversationsOpen?: number;
  /** KPI 11 — espera média (segundos) por party (ADR-106). */
  awaitingAverage?: { customerSeconds: number | null; operatorSeconds: number | null };
  /** KPI 8 — taxa de retomada por gatilho (0..1), últimos 7d. null se nenhuma conversa conduzida. */
  triggerResumptionRate?: number | null;
}

// ─── Coluna A (atendentes — resumo presence/carga) ────────────────────────
export type SupervisorPresenceStatus = 'online' | 'away' | 'offline';

export interface ColumnAEntry {
  userId: string;
  presenceStatus: SupervisorPresenceStatus;
  currentLoad: number;
  capacity: number;
  // ─── Item 317 Onda A (opcionais — retrocompat até o backend deployar) ───
  name?: string;
  role?: string;
  lastActivityAt?: string | null;
  modeDistribution?: { h_a: number; h_b: number; h_c: number };
  iaConducting?: number;
  flags?: ('behavioral' | 'handoff-pending')[];
  behavioralPattern?: { label: string; severity: string } | null;
  coAttendance?: { role: string; targetName: string } | null;
  coAttendedBy?: { fromName: string } | null;
  /** SLA per-atendente — maior espera (segundos) aguardando POR ele (party=operator, ADR-106). null se nenhuma. (Item 317) */
  slaSeconds?: number | null;
  /** Status semafórico do SLA: green ≤60s · amber 60–120s · red >120s. null se sem dado. */
  slaStatus?: 'green' | 'amber' | 'red' | null;
}

// ─── Coluna B (priority queue auto-curada) ────────────────────────────────
export const PRIORITY_ITEM_TYPES = [
  'conflict_alert',
  'sla_breach_2x',
  'pendency_4h_overdue',
  // Promessa do OPERADOR/IA ao cliente, vencida (Pendency
  // source='conversation-promise'). Reclassificação do critério 3 no Engine sob
  // `LUME_PROMISE_BREACH_ENABLED` — mutuamente exclusiva com
  // `pendency_4h_overdue`. NÃO confundir com `commitment_broken` (compromisso
  // do CLIENTE, CustomerCommitment/ADR-105).
  'promise_broken',
  'commitment_broken',
  'no_show_24h',
] as const;

export type PriorityItemType = (typeof PRIORITY_ITEM_TYPES)[number];

/**
 * Todos os 5 tipos são ATIVOS: 4 (commitment_broken, CustomerCommitment.status='broken')
 * e 5 (no_show_24h, Appointment.status='no_show') deixaram de ser stub — o Engine emite
 * itens reais desses tipos (v3.601). Mantido como função (e não `true`) para o caso de um
 * tipo futuro voltar a ser stub sem quebrar os call-sites.
 */
export function isActivePriorityType(_t: PriorityItemType): boolean {
  return true;
}

export interface PriorityItem {
  type: PriorityItemType;
  score: number;
  ref: string;
  severity?: string;
  conversationId?: string;
  resourceId?: string;
  // ─── Item 317 Onda D — campos humanos (opcionais — retrocompat) ───────────
  summary?: string;
  alertType?: string;
  chainPosition?: string;
  detectedAt?: string;
  personName?: string;
  /** Pendency — nome do operador dono (User.name). */
  operatorName?: string;
  waitingSeconds?: number;
  overdueSeconds?: number;
}

// ─── Coluna C (contadores de alertas de supervisão) ───────────────────────
export interface ColumnC {
  p1ActiveCount: number;
  overridesPendingCount: number;
  sequencesAtRiskCount: number;
  conflictAlertsOpenCount: number;
  insightsPendingSupervisorCount: number;
  // ─── Item 317 Onda D — contadores adicionais (opcionais — retrocompat) ─────
  conversationsQueuedOver5minCount?: number;
  attendantsAwayCount?: number;
  behavioralPatternsCriticalCount?: number;
  criticalChecklistPendingCount?: number;
  analysisContestationsPendingCount?: number;
}

// ─── Seções expandíveis ───────────────────────────────────────────────────
export interface BondsActivityEntry {
  bondId: string;
  conversationsCount: number;
  lastActivityAt: string | null;
  /** conversationId da conversa mais recente do Bond (maior updatedAt), roteável em
   *  /atendimento/conversation/:id (aba Cliente = superfície do Bond). null quando o backend
   *  não resolveu conversa → honest-defer do "Ver Bond detail" no VM (Item 355). */
  conversationId: string | null;
  /** Tipo do Bond (BondType) — Item 317 (opcional, retrocompat). */
  kind?: string;
  /** Titular: nome do participante primário. Item 317 (opcional, retrocompat). */
  titular?: string | null;
  /** Bond.adversarialMode !== 'none' (Item 317 Onda E). */
  adversarial?: boolean;
  /** Quantidade de ConversationSequence do bond (Item 317 Onda E). */
  sequencesCount?: number;
  /** Nomes de flags truthy das sequences do bond (highVolume/escalating/…) — Item 317 Onda E. */
  flags?: string[];
  /** Quantidade de ConflictAlerts critical envolvendo o bond — Item 317 Onda E. */
  criticalConflictAlerts?: number;
}

export interface SequenceRiskFlags {
  highVolumeFlag: boolean;
  allAbandonedFlag: boolean;
  escalatingSentimentFlag: boolean;
  crossChannelInsistenceFlag: boolean;
}

export interface SequenceRiskEntry {
  sequenceId: string;
  bondId: string;
  personId: string | null;
  /** conversationId da conversa atual (maior orderIndex — a mais recente, a mesma
   *  que deriva o sentiment) p/ o salto "Ver conversa atual". null quando a sequence
   *  não tem membros/conversa resolvível → honest-defer no VM (Item 354). */
  conversationId: string | null;
  totalConversations: number;
  lastConversationAt: string;
  flags: SequenceRiskFlags;
  // ─── Item 317 Onda E (opcionais — retrocompat) ───
  /** Nome da Person da sequence (Person.displayName). */
  personName?: string;
  /** Label humanizado do BondType do bond da sequence. */
  bondLabel?: string;
  /** Sentimento derivado da conversa mais recente. Ausente se sem dado limpo. */
  sentiment?: string;
  /** Atendente da sequence + presence. null se nenhum. */
  attendant?: { name: string; presence: 'online' | 'away' | 'offline' } | null;
}

// ─── Agregador /state ─────────────────────────────────────────────────────
export interface SupervisorDashboardState {
  kpis: SupervisorDashboardKPIs;
  columnA: ColumnAEntry[];
  columnB: PriorityItem[];
  columnC: ColumnC;
  bondsActivity: BondsActivityEntry[];
  sequencesRisk: SequenceRiskEntry[];
  computedAt: string;
  // ─── Item 317 Onda C: sparklines + deltas por KPI (opcional — retrocompat) ───
  kpiTrends?: Record<
    string,
    { sparkline: number[]; delta: number | null; trend: 'up' | 'down' | 'flat' }
  >;
}

// ─── Widgets (customização per-user — Princípio 3.6 via S10) ──────────────
export interface WidgetLayoutEntry {
  widgetId: string;
  position: number;
  isCollapsed: boolean;
  customConfig: Record<string, unknown>;
}

export interface SetWidgetPreferencePayload {
  position?: number;
  isCollapsed?: boolean;
  customConfig?: Record<string, unknown>;
}

// ─── Responses auxiliares ─────────────────────────────────────────────────
export interface KpisResponse {
  kpis: SupervisorDashboardKPIs;
}

export interface PriorityQueueResponse {
  items: PriorityItem[];
}

export interface WidgetLayoutResponse {
  widgets: WidgetLayoutEntry[];
}

export interface ResetLayoutResponse {
  rowsRemoved: number;
}

export interface RefreshResponse {
  accepted: true;
  computedAt: string;
}

export interface SnapshotsHistoryResponse {
  snapshots: { id: string; computedAt: string }[];
}

// ── Tela 7 Centro de Alertas — contrato /v1/conflict-alerts/v2 + /metrics (PR S5/S4) ──
// R15: lido de apps/engine/src/modules/adversarial/conflict-alerts/ (controllers + prisma).
export type ConflictSeverity = 'low' | 'medium' | 'high' | 'critical';

export type ConflictAlertStatus =
  | 'detected'
  | 'mitigated'
  | 'accepted_with_justification'
  | 'false_positive'
  | 'resolved'
  | 'revoked'
  | 'auto_resolved';

export type ConflictCategory =
  | 'category_a_societario'
  | 'category_b_papel_duplo'
  | 'category_c_informacao_privilegiada'
  | 'category_d_exclusividade'
  | 'category_e_capacidade_limitada';

export interface ConflictAlertRaw {
  id: string;
  tenantId: string;
  // `| string`: degrada gracioso p/ valores legados (Fatia C) não-catalogados.
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  category: ConflictCategory | string;
  severity: ConflictSeverity;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  status: ConflictAlertStatus | string;
  bondsInvolved: string[];
  participantsInvolved: string[];
  detectionContext: string | null;
  rationale: string | null;
  conversationId: string | null;
  operatorUserId: string | null;
  clientId: string | null;
  chainOriginId: string | null;
  chainPosition: number;
  previousAlertId: string | null;
  detectionConfidence: string | null; // Prisma Decimal(4,3) → serializa como string; adapter T7-2 coage Number()
  detectionAgent: string | null;
  detectionVersion: string | null;
  mitigationType: string | null;
  mitigationDescription: string | null;
  mitigationEvidenceDocumentIds: string[];
  mitigatedByUserId: string | null;
  mitigatedAt: string | null;
  acceptanceJustification: string | null;
  acceptanceType: 'temporary' | 'permanent' | null;
  acceptanceExpiresAt: string | null;
  acceptanceDocumentationIds: string[];
  acceptedByUserIds: string[];
  // Item 322 faceta a — sinal "Reopened" (badge na lista de Ativos). reopenedAt
  // != null ⇒ alerta foi reaberto de um estado terminal.
  reopenedAt: string | null;
  reopenedByUserId: string | null;
  pausedConversationIds: string[];
  blockedMcpCallsCount: number;
  slaThresholdMinutes: number | null;
  slaBreachedAt: string | null;
  assignedToUserId: string | null;
  resolvedByUserId: string | null;
  createdAt: string;
  resolvedAt: string | null;
  version: number;
  partiesRedacted?: boolean;
}

// R15: campos exatos de `model ConflictAlertChainHistory` (schema.prisma).
// transitionFrom/transitionReason são NOT NULL; o timestamp é `transitionedAt` (não createdAt).
export interface ConflictAlertChainHistoryEntry {
  id: string;
  transitionFrom: string;
  transitionTo: string;
  transitionedByUserId: string | null;
  transitionReason: string;
  operationId: string | null;
  transitionedAt: string;
}

// R15: campos exatos de `model MCPBlockEvent` (schema.prisma) — controller retorna o objeto cru.
export interface McpBlockEventRaw {
  id: string;
  conflictAlertId: string;
  conversationId: string;
  toolName: string;
  reason: string;
  attemptedAt: string;
}

export interface ConflictAlertMetricsDailyRaw {
  tenantId: string;
  date: string;
  totalDetected: number;
  totalMitigated: number;
  totalAccepted: number;
  totalFalsePositive: number;
  totalResolved: number;
  totalRevoked: number;
  avgMitigationTimeMinutesBySeverity: Partial<Record<ConflictSeverity, number>>;
  topTypes: { type: string; count: number }[];
  reincidenceRate: string; // Prisma Decimal(5,4) → serializa como string; adapter coage Number()
  severityDistribution: Partial<Record<ConflictSeverity, number>>;
}

export interface ConflictAlertListResponse {
  data: ConflictAlertRaw[];
}
export interface ConflictAlertHistoryResponse {
  data: ConflictAlertRaw[];
  nextCursor: string | null;
}

// DTOs de mutation (casam com os DTOs do lifecycle controller — spec §4.7)
export interface MitigateConflictAlertBody {
  mitigationType: string;
  mitigationDescription: string;
  evidenceDocumentIds: string[];
  notifyOperator?: boolean;
  notifyClient?: boolean;
  notifyManager?: boolean;
}
export interface AcceptWithJustificationBody {
  justification: string;
  acceptanceType: 'temporary' | 'permanent';
  durationDays?: number;
  documentationDocumentIds: string[];
}
export interface MarkFalsePositiveBody {
  feedback: string;
}
export interface ResolveConflictAlertBody {
  reason: string;
}
// Item 322 faceta a — reabre alerta terminal (resolved/mitigated/false_positive)
// → volta a 'detected' com flag Reopened. reason mínimo 10 chars (gate do modal).
export interface ReopenConflictAlertBody {
  reason: string;
}

// ─── Tela 4 · Inbox do Time (Pendency ADR-109 — R15 triangulado) ──────────
// Contrato real:
//   apps/engine/src/modules/pendencies/pendencies.serialization.ts (SerializedPendency)
//   apps/engine/src/modules/pendencies/aggregation/pendency-aggregation.service.ts
// `category`/`postponeCount` expostos no wire pelo PR T4-B.

/** Wire de GET /v1/pendencies — itens individuais (não o agregador /team). */
export interface SerializedPendencyRaw {
  id: string;
  tenantId: string;
  conversationId: string | null;
  bondId: string | null;
  title: string;
  description: string | null;
  status: 'open' | 'in_progress' | 'resolved' | 'cancelled' | 'expired';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  ownerUserId: string;
  delegatedToUserId: string | null;
  deadline: string | null;
  resolvedAt: string | null;
  resolvedByUserId: string | null;
  resolutionNote: string | null;
  source: string;
  aiAgentId: string | null;
  category: string | null;
  postponeCount: number;
  /** Flag derivada query-time (D109.2). */
  overdue: boolean;
  /** Person.displayName via conversation (Engine; null sem fonte — nunca fake). */
  customerName: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** Wire de GET /v1/team/pendencies/metrics. */
export interface PendencyTeamMetricsRaw {
  oldestOverdueMinutes: number | null;
  operatorWithMostOverdue: { userId: string; count: number } | null;
  categoryBottleneck: { category: string; count: number } | null;
  totalOpen: number;
  totalOverdue: number;
}

/** Célula de GET /v1/team/pendencies/heatmap (dayOfWeek 0=Dom..6=Sáb). */
export interface PendencyHeatmapCellRaw {
  ownerUserId: string;
  dayOfWeek: number;
  count: number;
  hasOverdue: boolean;
}

// ── Tela 5 Painel de SLA (S2) — wire contract R15 vs apps/engine/src/modules/sla/ ──
export interface SlaDashboardCardRaw {
  slaDefinitionId: string;
  slaType: string;
  cumprimento: number; // FRAÇÃO 0-1
  totalDemands: number;
  violations: number;
  trend: 'up' | 'down' | 'stable';
}
export interface SlaTopMetricsRaw {
  overallCumprimento: number; // fração 0-1
  totalBreachesLast24h: number;
  worstSlaType: string | null;
}
export interface SlaDashboardResultRaw {
  cards: SlaDashboardCardRaw[];
  topMetrics: SlaTopMetricsRaw;
}
export interface SlaHeatmapCellRaw {
  dayOfWeek: number; // 0-6, 0=Dom
  hourPeriod: number; // 0-23
  cumprimento: number; // fração 0-1
  totalCount: number;
}
export interface SlaRankingEntryRaw {
  userId: string;
  /** Nome real do User (Engine; null quando não resolve). */
  userName: string | null;
  violationCount: number;
  percentage: number; // fatia do operador no total de violações (NÃO compliance %)
  trend: 'up' | 'down' | 'stable'; // up = MAIS violações = pior
}
export type SlaSeverityRaw = 'warning' | 'critical';
export interface SlaBreachRaw {
  id: string;
  tenantId: string;
  slaDefinitionId: string;
  resourceType: 'pendency' | 'conversation' | 'conflict_alert' | 'appointment' | 'commitment';
  resourceId: string;
  occurredAt: string;
  resolvedAt: string | null;
  durationOverThresholdMinutes: number;
  assignedToUserId: string | null;
  /** Nome real do operador (Engine; null sem owner/não resolvido). */
  assignedToUserName: string | null;
  severity: SlaSeverityRaw;
  createdAt: string;
}
export interface SlaTemporalPatternRaw {
  id: string;
  tenantId: string;
  slaDefinitionId: string;
  patternType: string;
  dayOfWeek: number | null;
  hourPeriod: number | null;
  severity: SlaSeverityRaw;
  detectedAt: string;
  dismissedAt: string | null;
  dismissedByUserId: string | null;
}
export interface SlaDefinitionRaw {
  id: string;
  tenantId: string;
  slaType: string;
  scope: 'global' | 'per_bond_type' | 'per_playbook';
  scopeId: string | null;
  thresholdMinutes: number;
  status: 'active' | 'paused';
  version: number;
  createdAt: string;
  updatedAt: string;
}

// ── Tela 6 Dashboard de Qualidade (S3/S16) — wire contract ──
// R15 vs apps/engine/prisma/schema.prisma (QualityMetricsDaily L4269,
// OperatorQualityMetricsDaily L4293, PlaybookQualityMetricsDaily L4318,
// AgentPerformanceDaily L2098) + quality.controller.ts.
// Todos os campos Prisma `Decimal` serializam STRING no JSON → adapter coage.
// avgSentiment/sentimentAvg/sentimentPostAvg = Dec(4,2) escala -1..1 (cron usa
// thresholds ±0.2 na distribuição → D4). Demais taxas = Dec(5,4) fração 0-1.
export interface QualityGlobalRaw {
  id: string;
  tenantId: string;
  date: string;
  periodType: 'daily' | 'weekly' | 'monthly';
  avgSentiment: string; // Dec(4,2) escala -1..1
  sentimentDistribution: { positive: number; neutral: number; negative: number }; // frações 0-1
  reopeningRate7d: string; // Dec(5,4) fração
  reopeningRate30d: string; // Dec(5,4) fração
  avgResolutionTimeMinutes: number; // Int (cron v1 grava 0 placeholder)
  behaviorPostAttendance: Record<string, number>; // Json livre (cron v1 grava {})
  computedAt: string;
}
export interface OperatorQualityRaw {
  id: string;
  tenantId: string;
  operatorUserId: string;
  /** Nome real do operador (Engine; null quando não resolve). */
  operatorName: string | null;
  date: string;
  conversationCount: number;
  sentimentAvg: string; // Dec(4,2) -1..1
  slaCompliance: string; // Dec(5,4) fração
  suggestionAcceptRate: string; // Dec(5,4) fração
  pendenciesResolved: number;
  pendenciesOverdue: number;
  computedAt: string;
}
export interface PlaybookQualityRaw {
  id: string;
  tenantId: string;
  playbookId: string;
  date: string;
  usageCount: number;
  conclusionRate: string; // Dec(5,4) fração
  sentimentPostAvg: string; // Dec(4,2) -1..1
  durationAvgMinutes: number;
  computedAt: string;
}
export interface AgentPerformanceRaw {
  id: string;
  tenantId: string;
  agentId: string;
  date: string;
  invocations: number;
  tokensTotal: number;
  // B-1 (17/Set/2026): `costTotalUsd` saiu do wire. É o custo da plataforma, e
  // esta leitura é gateada por `agent-performance-read`, capability de tenant.
  // Schema declara Dec(5,4) NOT NULL → wire sempre string; tipamos `| null`
  // defensivamente (D8 — degradação honesta caso o cron passe a emitir null).
  acceptanceRate: string | null;
  avgLatencyMs: number;
  computedAt: string;
}

/**
 * Consumo por conversa (Qualidade IA do Gestor) — `GET /v1/ai-cost-monitoring/top-conversations`.
 * Item individual do ranking. B-1 (17/Set/2026): era custo; virou tokens, e a
 * ordenação do backend mudou junto. A URL mantém o nome antigo.
 */
export interface TopConversationCost {
  conversationId: string;
  totalTokens: number;
  logicalInvocations: number;
  agentCount: number;
}

/** Resposta de `GET /v1/ai-cost-monitoring/top-conversations`. */
export interface TopConversationsCostResponse {
  tenantId: string;
  range: { from: string; to: string };
  results: TopConversationCost[];
}

/**
 * Consumo por agente de uma conversa — item de
 * `GET /v1/ai-cost-monitoring/conversations/:conversationId/cost-by-agent`.
 */
export interface ConversationAgentCost {
  agentId: string;
  logicalInvocations: number;
  tokensInput: number;
  tokensOutput: number;
  totalTokens: number;
}

/** Resposta de `GET /v1/ai-cost-monitoring/conversations/:conversationId/cost-by-agent`. */
export interface ConversationCostByAgentResponse {
  tenantId: string;
  conversationId: string;
  results: ConversationAgentCost[];
  totalTokens: number;
}

/**
 * Comparação entre modelos (Qualidade IA do Gestor) — `GET /v1/ai-cost-monitoring/by-model`.
 * Percentis de latência calculados em SQL; `latency*Ms` é null quando nenhuma
 * invocação do modelo registrou latência no período.
 */
export interface AiModelStatRaw {
  modelUsed: string;
  invocations: number;
  successfulInvocations: number;
  tokensInput: number;
  tokensOutput: number;
  totalTokens: number;
  latencyP50Ms: number | null;
  latencyP95Ms: number | null;
  latencyAvgMs: number | null;
}

/** Dashboard Qualidade §5 — ranking de atendentes por sequences abandonadas. */
export interface TopAttendantAbandonedRaw {
  userId: string;
  name: string;
  count: number;
}

/** Dashboard Qualidade §5 — ranking de Bonds por sequences críticas. */
export interface TopBondCriticalRaw {
  bondId: string;
  label: string;
  count: number;
}

/** Dashboard Qualidade §7 — taxa de resolução por faixa de trocas de playbook. */
export interface ResolutionBySwitchBucketRaw {
  bucket: '0' | '1' | '2+';
  total: number;
  resolved: number;
  resolutionRate: number;
}

// ── Tela 6 T6-2 Análises (Sequences / Postura / Derivas) — wire contract ──
// R15 vs schema.prisma (BehavioralPattern L4106, BehavioralPatternContestation
// L4151, PlaybookDriftMatrixDaily L4342) + behavioral-pattern.controller.ts +
// quality.controller.ts + sequence-flags-aggregation.service.ts. Controllers
// devolvem a entidade Prisma crua (sem DTO de serialização).

/** GET /v1/quality/metrics/sequences → só counts (Int). */
export interface SequenceFlagsRaw {
  totalSequences: number;
  highVolumeCount: number;
  allAbandonedCount: number;
  escalatingSentimentCount: number;
  crossChannelInsistenceCount: number;
}

/** GET /v1/behavioral-patterns → { items, nextCursor }. patternType = 8 enums. */
export interface BehavioralPatternRaw {
  id: string;
  tenantId: string;
  operatorUserId: string;
  patternType: string; // defensive_tone | interrupting | lack_of_empathy | slow_response | script_deviation | aggressive_tone | inappropriate_humor | lack_of_validation
  severity: 'low' | 'medium' | 'high' | 'critical';
  detectedCount: number;
  firstDetectedAt: string;
  lastDetectedAt: string;
  trend: 'stable' | 'increasing' | 'decreasing';
  status: 'active' | 'dismissed' | 'under_review';
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
}

/** GET /v1/behavioral-patterns/contestations → { items, nextCursor }.
 *  status = pending | upheld | overturned (NÃO approved/rejected). */
export interface BehavioralContestationRaw {
  id: string;
  behavioralPatternId: string;
  operatorUserId: string;
  contestationReason: string; // enum no schema; só consumimos status/operatorUserId
  status: 'pending' | 'upheld' | 'overturned';
  createdAt: string;
}

/** GET /v1/quality/metrics/playbook-drift → array flat. resolutionRate Dec(5,4) string. */
export interface PlaybookDriftRaw {
  id: string;
  tenantId: string;
  date: string;
  fromPlaybookId: string | null;
  toPlaybookId: string;
  transitionCount: number;
  resolutionRate: string; // Dec(5,4) fração
}

// ── Tela 8 Painel de Gestão de P1 (módulo p1 — R15 controllers/services/schema) ──
// Enums espelham schema.prisma (P1Category/P1Status/P1EscalationLevel). O `list`
// retorna o modelo P1Incident CRU, sem relações (DA — sem cliente/bond/atendente/desc).
export type P1Category =
  | 'csam'
  | 'threat'
  | 'level4_cross_conv'
  | 'conflict_alert_critical'
  | 'lgpd_critical'
  | 'anonymous_report';
export type P1Status = 'active' | 'acknowledged' | 'handled' | 'resolved' | 'closed';
export type P1EscalationLevel = 'attendant' | 'supervisor' | 'manager' | 'director';
export type P1ChainReason =
  | 'auto-escalated'
  | 'bypassed-offline-attendant'
  | 'manual-override'
  | 'detected';

/** Entry append-only de `escalationChainHistory` (Json no schema). */
export interface P1ChainEntryRaw {
  level: P1EscalationLevel;
  score: number;
  transitionedAt: string;
  reason: P1ChainReason;
}

/** Modelo P1Incident cru (sem relações). detectionConfidence Dec(4,3)→string (D4). */
export interface P1IncidentRaw {
  id: string;
  category: P1Category;
  conversationId: string;
  sequenceId: string | null;
  detectedByDetector: string;
  detectionConfidence: number | string;
  currentScore: number;
  currentEscalationLevel: P1EscalationLevel;
  escalationChainHistory: unknown; // Json — validar/parsear no adapter (D4)
  status: P1Status;
  acknowledgedByUserId: string | null;
  acknowledgedAt: string | null;
  handledByUserId: string | null;
  resolvedAt: string | null;
  runbookExecutionId: string | null;
  bypassedAttendantOfflineFlag: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface P1ListResponse {
  items: P1IncidentRaw[];
  nextCursor: string | null;
}
export interface P1VolumeStatusRaw {
  activeCount: number;
  threshold: number;
  critical: boolean;
}
export interface P1TimelineMiniItemRaw {
  conversationId: string;
  channel: string;
  status: string;
  startedAt: string;
  closedAt: string | null;
  orderIndex: number;
}
export interface P1TimelineMiniResultRaw {
  sequenceId: string;
  totalConversations: number;
  flags: Record<string, unknown>;
  items: P1TimelineMiniItemRaw[];
}

// ── Tela 2 Acompanhamento Silencioso (módulo peer-coordination) — wire contract ──
// R15 confirmado contra os controllers/services/DTOs reais:
//   apps/engine/src/modules/peer-coordination/co-attendants/coattendance-session.controller.ts
//   .../co-attendants/co-attendants.service.ts (CoAttendantListItem, startSession 409)
//   .../co-attendants/services/quality-indicators-live.service.ts (QualityIndicators)
//   .../co-attendants/services/conversation-composer-sync.service.ts (ConversationComposerState)
//   apps/engine/prisma/schema.prisma (ConversationComposerState)
//   apps/engine/src/modules/websocket/websocket.types.ts (CoAttendantRole + event strings)

/** Os 3 modos canônicos da sessão de co-atendimento (schema + websocket.types). */
export type CoAttendantRole = 'shadow_observer' | 'collaborative_observer' | 'supervisor_monitor';

/**
 * Item de GET /v1/coattendance/active — só as sessões do PRÓPRIO supervisor (D1).
 * Espelha `CoAttendantListItem` do co-attendants.service (toListItem).
 */
export interface CoAttendantListItem {
  id: string;
  conversationId: string;
  userId: string;
  role: CoAttendantRole;
  addedByUserId: string;
  addedAt: string;
  removedAt: string | null;
  removedByUserId: string | null;
}

/**
 * Wire de GET /v1/conversations/:id/composer-state.
 * Estado normal: `ConversationComposerState` (conversationId/userId/currentText/lastUpdatedAt).
 * Fallback (sem row / cross-tenant): `{conversationId, currentText:'', userId:null}` — sem lastUpdatedAt.
 */
export interface ConversationComposerStateWire {
  conversationId: string;
  userId: string | null;
  currentText: string;
  lastUpdatedAt?: string;
}

/** Tempo de resposta do atendente (segundos) — `ResponseTimeKpi` real. */
export interface QualityResponseTime {
  avgSec: number;
  p50Sec: number;
  p95Sec: number;
  sampleSize: number;
}

/**
 * Wire de GET /v1/conversations/:id/quality-indicators (`QualityIndicators` real).
 * sentiment = enum (não score /10); adherence/usage = fração 0..1 (não breakdown).
 */
export interface QualityIndicators {
  sentiment: 'positive' | 'neutral' | 'negative' | 'hostile';
  playbookAdherence: number;
  aiSuggestionUsage: number;
  responseTime: QualityResponseTime;
  computedAt: string;
}

/**
 * Wire de GET /v1/conversations/:id/discrepancy-indicator (consumido na T2-2).
 * Só bond adversarial; fora disso o backend devolve `{discrepancyApplicable:false}` (regra do silêncio).
 */
export type DiscrepancyIndicatorWire =
  | { supervisorVisibleCount: number; attendantVisibleCount: number; discrepancyCount: number }
  | { discrepancyApplicable: false };

/**
 * Wire de POST/GET /v1/coattendance/:id/coaching-message(s) — canal de coaching
 * DIRIGIDO (supervisor↔atendente). Espelha `model CoachingMessage` (schema.prisma):
 * sem tenantId no payload. timestamp = ISO. Cursor por `timestamp` (after&limit).
 */
export interface CoachingMessage {
  id: string;
  coAttendanceId: string;
  fromUserId: string;
  toUserId: string;
  text: string;
  visibleToAttendant: boolean;
  timestamp: string;
}

/**
 * Wire de POST/GET /v1/coattendance/:id/session-note. Upsert (unique
 * coAttendanceId+supervisorUserId), incrementa `version`. SEM If-Match (D6).
 * Espelha `model SupervisorSessionNote` (schema.prisma) — sem tenantId no payload.
 * GET devolve 404 quando a nota ainda não foi criada → tratar como texto vazio.
 */
export interface SupervisorSessionNote {
  id: string;
  coAttendanceId: string;
  supervisorUserId: string;
  text: string;
  version: number;
  createdAt: string;
  lastUpdatedAt: string;
}

// ── Tela 2 T2-3 — Ações de força (módulo supervisor-actions) — wire contract ──
// R15 confirmado contra os controllers/DTOs reais:
//   apps/engine/src/modules/supervisor-actions/force-takeover/force-takeover.controller.ts
//     POST /v1/supervisor-actions/conversations/:id/force-takeover {reason(≥20), mode?(≤50)}
//       → ConversationTakeover (Prisma cru). cap `force-takeover`.
//     NOTA R15: o service IGNORA `mode` na v1 (DTO aceita p/ extensibilidade — controller
//     só repassa reason). Mandamos `mode` quando útil, mas hoje é no-op no backend.
//   apps/engine/src/modules/supervisor-actions/force-playbook-switch/force-playbook-switch.controller.ts
//     GET  .../available-playbooks → ARRAY CRU {id,name,version,vertical}[] (NÃO {data}).
//     POST .../force-playbook-switch {newPlaybookId(≤64), reason(≥20)} → PlaybookChangeHistory.
//       cap `force-playbook-switch` (≠ `force-playbook`).

/** Item de GET /v1/supervisor-actions/conversations/:id/available-playbooks (array cru). */
export interface AvailablePlaybook {
  id: string;
  name: string;
  version: number;
  vertical: string;
}

/**
 * Subconjunto observável de `ConversationTakeover` (resposta do force-takeover /
 * return / transfer). Só consumimos o necessário p/ derivar o desfecho honesto
 * (id + motivo + tempos) e o evento centralizado da Tela 3 (Intervenção).
 * Campos não-consumidos do Prisma ficam fora — não inventamos shape; os opcionais
 * abaixo refletem colunas reais de `conversation_takeovers` (schema.prisma 2987+).
 */
export interface ConversationTakeoverResult {
  id: string;
  conversationId: string;
  status?: string;
  reason?: string;
  createdAt?: string;
  /** Colunas reais do Prisma — derivam o evento de takeover (D6) e o desfecho. */
  executedAt?: string;
  returnedAt?: string | null;
  returnReason?: string | null;
  takenOverByUserId?: string;
  originalAssignedToUserId?: string | null;
  transferTargetUserId?: string | null;
  transferReason?: string | null;
}

/**
 * Subconjunto observável de `PlaybookChangeHistory` (resposta do force-playbook-switch).
 * Só consumimos o necessário p/ derivar o desfecho honesto.
 */
export interface PlaybookChangeResult {
  id: string;
  conversationId?: string;
  newPlaybookId?: string;
  previousPlaybookId?: string | null;
  reason?: string;
  createdAt?: string;
}

// ── Tela 9 Aprovação de Overrides (módulo overrides S8 + dual-approval S6) ──
// R15: enum snake_case (schema.prisma:4734). O list retorna OverrideRequest CRU,
// sem relações (DA — nome do solicitante via usePresence; conversa por #conversationId).
export type OverrideType =
  | 'ai_required_validation'
  | 'carencia_proativa'
  | 'gatilho_protecao'
  | 'modo_operacao'
  | 'capability_temporaria'
  | 'legal_capacity_exception'
  | 'conflict_alert_accept'
  | 'h_c_adversarial_strict';

export type OverrideStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'expired'
  | 'awaiting_dual_approval';

/** Modelo OverrideRequest cru (sem relações). contextPayload Json polimórfico por tipo. */
export interface OverrideRequestRaw {
  id: string;
  overrideType: OverrideType;
  requesterUserId: string;
  conversationId: string | null;
  contextResourceType: string;
  contextResourceId: string;
  reason: string;
  contextPayload: unknown; // Json — ler defensivamente no adapter
  status: OverrideStatus;
  approverUserId: string | null;
  approvalDecision: string | null;
  approvalNote: string | null;
  approvedAt: string | null;
  dualApprovalRequestId: string | null;
  expiresAt: string | null;
  operationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OverrideListResponse {
  items: OverrideRequestRaw[];
  nextCursor: string | null;
}

/** DualApprovalRequest cru — só o necessário p/ o banner condicional (D6) e card Teia (Painel V3). */
export interface DualApprovalRequestRaw {
  id: string;
  requestType: string;
  status: string;
  contextResourceType: string;
  contextResourceId: string;
  expiresAt: string; // ISO — presente no schema (backend DateTime Timestamptz)
  createdAt: string;
}

/** Resultado do approve-type-5 (subconjunto observável do TemporaryFeatureFlag). */
export interface TemporaryFeatureFlagRaw {
  id: string;
  capability: string;
  effectiveUntil: string;
  status: string;
}

/** Desfecho dos approve-type-7/8 — pode cair em dupla aprovação (D6). */
export interface OverrideDualResult {
  overrideStatus: OverrideStatus;
  dualApprovalRequestId: string | null;
}

// ── Tela 10 Insights Estratégicos (módulo gestor-insight-agent S9) ──
// R15: enums snake_case (schema.prisma:4907-4947). O list retorna Insight CRU, sem relações
// (nome de targetUserId via usePresence). confidence é Decimal(4,3) → chega como STRING no JSON.
export type InsightLens = 'supervisor' | 'gestor';

export type InsightCategory = 'operational' | 'learning' | 'compliance' | 'commercial';

export type InsightStatus = 'pending' | 'accepted' | 'in_action' | 'false_positive' | 'archived';

/** Model Insight cru (sem relações). confidence string (Decimal), drillDownData Json livre. */
export interface InsightRaw {
  id: string;
  lens: InsightLens;
  category: InsightCategory;
  title: string;
  content: string;
  confidence: string; // Decimal(4,3) → coagir com Number() no adapter
  detectionPeriod: string;
  basedOnCount: number;
  /** Item 478 — decidido pelo engine: basedOnCount < piso do tenant. Ausente em engine antigo. */
  insufficientBasis?: boolean;
  drillDownData: unknown; // Json — ler defensivamente no adapter
  detectorAgentId: string;
  detectorVersion: string;
  status: InsightStatus;
  targetUserId: string | null;
  createdAt: string;
  acceptedAt: string | null;
  actionTakenAt: string | null;
  archivedAt: string | null;
}

export interface InsightListResponse {
  items: InsightRaw[];
  nextCursor: string | null;
}

/** Métricas agregadas (subtítulo "N aceitos hoje" — D8). */
export interface InsightAggregateMetrics {
  totalGenerated: number;
  totalAccepted: number;
  totalActionTaken: number;
  totalFalsePositive: number;
  totalArchived: number;
}

// ── Tela 10 T10-2 decisões (enums snake_case + payloads de escrita) ──
export type InsightActionType =
  | 'message_team'
  | 'schedule_meeting'
  | 'create_pendency_self'
  | 'create_pendency_manager'
  | 'coach_schedule';

export type InsightFalseFeedbackReason =
  | 'incorrect_data'
  | 'inadequate_conclusion'
  | 'already_treated'
  | 'missing_context'
  | 'other';

export type SensitivityMode = 'conservative' | 'balanced' | 'aggressive';

/** Resultado de POST /:id/action. `linkedResourceId` só populado nos create_pendency_* (D5). */
export interface InsightActionRaw {
  id: string;
  insightId: string;
  actionType: InsightActionType;
  payload: unknown;
  linkedResourceId: string | null;
  status: string;
  createdAt: string;
}

/** Job de geração manual (poll) — subconjunto observável (D7). */
export interface InsightGenerationJobRaw {
  id: string;
  status: string; // pending | running | completed | failed
  insightsGenerated: number;
  duplicatesSuppressed: number;
  completedAt: string | null;
  errorDetails: string | null;
}
